import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { authApi } from "../apis/authApi";
import DattoScriptExportDialog from "./DattoScriptExportDialog";
import { DattoRmmGuideInner } from "./integrationGuides/DattoRmmGuideInner";
import DattoAccessFeedbackAlert, { DattoAccessCheck } from "./DattoAccessFeedbackAlert";

interface DattoSetupFormProps {
  onBack?: () => void;
  onSuccess?: () => void;
  onSkip?: () => void;
}

const DattoSetupForm: React.FC<DattoSetupFormProps> = ({ onBack, onSuccess, onSkip }) => {
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecretKey, setApiSecretKey] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    checks?: DattoAccessCheck[];
    has_warnings?: boolean;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const testedValuesRef = useRef<{
    apiUrl: string;
    apiKey: string;
    apiSecretKey: string;
  } | null>(null);

  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    return (
      apiUrl !== testedValuesRef.current.apiUrl ||
      apiKey !== testedValuesRef.current.apiKey ||
      apiSecretKey !== testedValuesRef.current.apiSecretKey
    );
  };

  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;
  const canTest = apiUrl.trim() && apiKey.trim() && apiSecretKey.trim();

  const handleTestConnection = async () => {
    if (!canTest) {
      setTestResult({
        success: false,
        message: "Please fill in all fields before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await authApi.testDattoConnection({
        api_url: apiUrl.trim().replace(/\/+$/, ""),
        api_key: apiKey,
        api_secret_key: apiSecretKey,
      });

      if (result.success) {
        testedValuesRef.current = { apiUrl: apiUrl.trim(), apiKey, apiSecretKey };
      }

      setTestResult({
        success: result.success,
        message: result.message || "Connection test successful!",
        checks: result.checks,
        has_warnings: result.has_warnings,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
        checks: error.response?.data?.checks,
      });
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      await authApi.saveDattoConfiguration({
        api_url: apiUrl.trim().replace(/\/+$/, ""),
        api_key: apiKey,
        api_secret_key: apiSecretKey,
      });

      setShowSuccessSnackbar(true);

      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || "Failed to save Datto RMM configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {onBack && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              background: "#f5f5f5",
            },
          }}
        >
          Back
        </Button>
      )}

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        Datto RMM Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          How to Get Datto RMM API Credentials
        </Typography>
        <DattoRmmGuideInner showGuideTitle={false} />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#fff",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 1,
            color: "#1a1a1a",
          }}
        >
          Theo Components
        </Typography>
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#6c6c6c", mb: 2, lineHeight: 1.6 }}>
          Export Datto-importable components for Theo Windows/macOS secure shell and script actions.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
            sx={{ fontFamily: "var(--theo-font-sans)", textTransform: "none", borderColor: "#705d4a", color: "#705d4a" }}
          >
            Export
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="API URL"
          value={apiUrl}
          onChange={(e) => {
            setApiUrl(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="https://vidal-api.centrastage.net"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
          helperText="Your Datto RMM API URL (e.g. https://vidal-api.centrastage.net)"
        />

        <TextField
          label="API Key"
          type="text"
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
          helperText="Your Datto RMM API Key"
        />

        <TextField
          label="API Secret Key"
          type="password"
          value={apiSecretKey}
          onChange={(e) => {
            setApiSecretKey(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
          helperText="Your Datto RMM API Secret Key (stored securely)"
        />

        {testResult ? <DattoAccessFeedbackAlert result={testResult} /> : null}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || !canTest}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 150,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>

          {onSkip && (
            <Button
              variant="text"
              onClick={onSkip}
              sx={{
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
                fontWeight: 500,
                color: "#6c6c6c",
                "&:hover": { background: "#f5f5f5" },
              }}
            >
              Skip for now
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          Datto RMM has been successfully connected!
        </Alert>
      </Snackbar>
      <DattoScriptExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </Box>
  );
};

export default DattoSetupForm;
